import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets

// services

// props

// components
import { Checkbox, DefaultButton, DetailsList, Dropdown, Panel, PanelType, PrimaryButton, SelectionMode, Stack, TextField, Toggle } from '@fluentui/react';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import LoadingComponent from '../../../../feedbacks/loading';
import { IDepositTransactionResourceProps } from '../../../../../props/finance/incomes/deposits/transactions';
import GeneralService from '../../../../../services/general';
import DepositTransactionService from '../../../../../services/finance/deposits/transaction';
import VendorsService from '../../../../../services/data/vendors';
import UserService from '../../../../../services/users/user';
import OutcomeService from '../../../../../services/finance/outcomes';
import ValidationService from '../../../../../services/validation';
import CompanyService from '../../../../../services/users/company';
import { IOutcomeOrderResourceShortProps } from '../../../../../props/finance/outcomes/orders';
import { IOutcomeResourceShorterProps } from '../../../../../props/finance/outcomes';
import OutcomeOrderService from '../../../../../services/finance/outcomes/orders';
import SelectOutcomeInvoice from '../../../../uiframeworks/forms/finances/outcomes/invoice';
import SelectVendor from '../../../../uiframeworks/forms/operationals/vendors/vendor';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import OrderService from '../../../../../services/orders/order';
import PermissionsService from '../../../../../services/permissions';
import PasswordConfirmation from '../../../../uiframeworks/confirmations/password';

type CreateDepositFormProps = {
    transactionId?: string;
    outcomeInvoiceId?: string;
    vendorId?: string;
    companyId?: string;
    employeeId?: string;
    onDismissed(refresh?: boolean): void;
}

const CreateDepositForm: React.FC<CreateDepositFormProps> = (props: CreateDepositFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [relatedTo, setRelatedTo] = React.useState<string>("");
    const [affectInvoiceAmount, setAffectInvoiceAmount] = React.useState<boolean>(false);
    const [data, setData] = React.useState<Partial<IDepositTransactionResourceProps>>({
        id: GeneralService.guid()
    });
    const [relatedOutcomeOrders, setRelatedOutcomeOrders] = React.useState<IOutcomeOrderResourceShortProps[] | undefined>();
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<any>({});
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const hasOverwriteOutcomeInvoiceAccess = PermissionsService.hasPermission(['outcomes.invoices.overwrite'], user.permissions);

    useEffect(() => {
        init();
    }, []);

    const _onRetrieveRelatedOutcomeOrders = async (invoice: IOutcomeResourceShorterProps) => {
        const orders = await OutcomeOrderService.retrieve(invoice.id);
        setRelatedOutcomeOrders(orders);
    }

    const init = async () => {
        try {
            let _data = data;
            let relatedTo = 'none';

            if (props.transactionId) {
                const _transaction = await DepositTransactionService.read(props.transactionId);
                _data = _transaction;

                if (_data.outcomeInvoice) {
                    _onRetrieveRelatedOutcomeOrders(_data.outcomeInvoice);
                }

                setData({ ..._data });
                setLoaded(true);

                return;
            }

            if (props.vendorId) {
                const _vendor = await VendorsService.get(props.vendorId);
                _data.vendor = _vendor;
            }

            if (props.employeeId) {
                const _user = await UserService.get(props.employeeId);
                _data.employee = _user;
            }

            if (props.companyId) {
                const _user = await CompanyService.get(props.companyId);
                _data.company = _user;
            }

            if (props.outcomeInvoiceId) {
                const _outcome = await OutcomeService.get(props.outcomeInvoiceId);
                _data.outcomeInvoice = _outcome;
                _data.vendor = _outcome.vendor;

                relatedTo = 'outcome';

                _onRetrieveRelatedOutcomeOrders(_data.outcomeInvoice);
            }

            setData({ ..._data });
            setRelatedTo(relatedTo);
            setLoaded(true);
        } catch (e) {

        }
    }

    const _onSubmit = async () => {
        try {
            setActiveSurface(undefined);
            setSubmitting(true);

            const {amount, reason, vendor, outcomeInvoice} = data;

            const fd = new FormData();
            fd.append('amount', amount || "0");
            fd.append('reason', reason || "");

            if (vendor) {
                fd.append('vendorId', vendor.id);
            }

            if (relatedTo === 'outcome' && outcomeInvoice) {
                fd.append('outcomeInvoiceId', outcomeInvoice.id);

                if (affectInvoiceAmount) {
                    const newInvoiceAmount = countNewOutcomeInvoiceAmount();
                    fd.append('newOutcomeInvoice', JSON.stringify({
                        id: outcomeInvoice.id,
                        ...newInvoiceAmount
                    }));
    
                    (relatedOutcomeOrders || []).forEach((outcomeOrder) => {
                        fd.append('outcomeOrders[]', JSON.stringify({
                            id: outcomeOrder.id,
                            amount: outcomeOrder.amount
                        }));
                    });
                }
            }

            await DepositTransactionService.create(fd);

            banner.add({
                text: `Rp. ${GeneralService.getNumberWithSeparator(data.amount || "0")} has been deposit successfully`,
                icon: faCheck,
                variant: 'success'
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    const isSubmitButtonDisabled = () => {
        if ((data.amount || "").trim() === "") {
            return true;
        } else if (error.amount) {
            return true;
        }

        return false;
    }

    const countLeftoverAmount = () => {
        let leftover = 0;

        if (relatedTo === 'outcome' && data.outcomeInvoice) {
            leftover = Number(data.outcomeInvoice.paid) - Number(data.amount || "0");
        }

        return leftover.toFixed(2);
    }

    const countNewOutcomeInvoiceAmount = () => {
        const outcome: IOutcomeResourceShorterProps = JSON.parse(JSON.stringify(data.outcomeInvoice));

        let result: {
            originalAmount: number;
            amount: number;
            totalAmount: number;
            ppn: number;
            pph: number;
        } = {
            originalAmount: 0,
            amount: 0,
            totalAmount: 0,
            ppn: 0,
            pph: 0
        };

        if (outcome) {
            const originalAmount = OutcomeService.calculate.originalAmountByInvoiceAmount({
                invoiceAmount: (Number(outcome.amount) - Number(data.amount || "0")),
                includePPh: outcome.includePPh || false,
                includePPn: outcome.includePPn || false,
                pph: Number(outcome.pph || "0"),
                ppn: Number(outcome.ppn || "0"),
                ppnPercentage: outcome.ppnPercentage,
                pphPercentage: outcome.pphPercentage
            });

            outcome.originalAmount = originalAmount.toFixed(2);
            result = {
                originalAmount: Number(outcome.originalAmount),
                ...OutcomeService.calculatePrice(outcome)
            }
        }

        return result;
    }

    const regenerateOutomeRelatedOrdersAmount = () => {
        if (relatedTo === 'outcome' && relatedOutcomeOrders && relatedOutcomeOrders.length === 1) {
            const newInvoiceAmount = countNewOutcomeInvoiceAmount();

            let _relatedOutcomeOrders = relatedOutcomeOrders;
            _relatedOutcomeOrders[0].amount = newInvoiceAmount.totalAmount.toFixed(2);

            setRelatedOutcomeOrders([..._relatedOutcomeOrders]);
        }
    }

    const outcomeRelatedOrdersAmountDifference = () => {
        const newInvoiceAmount = countNewOutcomeInvoiceAmount();
        let totalRelatedOrdersAmount = 0;

        (relatedOutcomeOrders || []).forEach((order) => {
            totalRelatedOrdersAmount += Number(order.amount);
        });

        return Number((totalRelatedOrdersAmount - newInvoiceAmount.totalAmount).toFixed(2));
    }

    return <Panel headerText={'Create Deposit'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed()}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <>
                {submitting ? <LoadingComponent label={"Creating deposit ..."} spinnerPosition={'baseline'} labelPosition={'right'} /> : null}
                {!submitting ? <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={() => setActiveSurface('password.confirm')} />
                    <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed() }} />
                </Stack> : null}
            </>
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <LoadingComponent label={"Retrieving form ..."} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
            {loaded ? <>
                <Dropdown label={"Select which invoice is this related to"}
                    required={true}
                    selectedKey={relatedTo}
                    disabled={true}
                    options={[
                        { key: "none", text: "Not related to any payments" },
                        { key: "outcome", text: "Invoice from vendor" },
                        { key: "income", text: "Invoice to customer" }
                    ]}
                    onChange={(evt, opt) => setRelatedTo(opt?.key as string)} />
                {relatedTo === 'outcome' ? <>
                    <Stack className={'divider'}></Stack>
                    <SelectOutcomeInvoice label={"Select invoice"}
                        disabled={props.outcomeInvoiceId !== undefined}
                        selected={data.outcomeInvoice}
                        onChange={(invoice) => {
                            data.outcomeInvoice = invoice;
                            data.vendor = invoice?.vendor;
                            setData({ ...data });
                        }} />
                    <SelectVendor label={"Select vendor"}
                        selected={data.vendor}
                        disabled={props.outcomeInvoiceId !== undefined}
                        onChange={(vendor) => {
                            data.vendor = vendor;
                            setData({ ...data });
                        }} />
                </> : null}
                {relatedTo === 'none' ? <>
                    <Stack className={'divider'}></Stack>
                    <SelectVendor label={"Select vendor"}
                        selected={data.vendor}
                        required
                        onChange={(vendor) => {
                            data.vendor = vendor;
                            setData({ ...data });
                        }} />
                </> : null}
                <Stack className={'divider'}></Stack>
                <TextField label={'Reason'}
                    value={data.reason || ""}
                    required={true}
                    multiline
                    rows={3}
                    resizable={false}
                    autoAdjustHeight
                    errorMessage={error.reason}
                    onChange={(evt, value) => {
                        data.reason = value || "";

                        const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 2000 });
                        error.reason = validation.message;

                        setData({ ...data });
                        setError({ ...error });
                    }} />
                <TextField label={'Deposit amount'}
                    value={data.amount}
                    prefix={'Rp'}
                    required={true}
                    errorMessage={error.amount}
                    onChange={(evt, value) => {
                        if ((value || "").trim() === "" || !isNaN(Number(value))) {
                            data.amount = value || "";

                            let max: number = 0;
                            if (relatedTo === 'outcome' && data.outcomeInvoice) {
                                max = Number(data.outcomeInvoice.paid + "");
                            }

                            const validation = ValidationService.combination(value, ['required', 'limit', 'number'], { maxChars: 20, max, min: 1 });
                            error.amount = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                            regenerateOutomeRelatedOrdersAmount();
                        }
                    }} />
                {relatedTo === 'outcome' && data.outcomeInvoice ? <>
                    <Stack className={'divider'}></Stack>
                    <TextField label={'Leftover paid amount'}
                        value={GeneralService.getNumberWithSeparator(countLeftoverAmount())}
                        prefix={'Rp'}
                        disabled={true} />
                    {!relatedOutcomeOrders ? <LoadingComponent label={'Retrieving orders related to this invoice ...'} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                    {relatedOutcomeOrders ? <>
                        <Stack tokens={{ childrenGap: 8 }}>
                            <Stack>
                                <Label size={'xsmall'}>Does this affect invoice amount and orders related to this invoice?</Label>
                                <Text size={'small'}>If the deposit amount will affect the amount of the invoice, please tick "This affect invoice amount" checkbox. By ticking this, it means the total amount for this invoice will be reduced to <b>Rp. {GeneralService.getNumberWithSeparator(countNewOutcomeInvoiceAmount().totalAmount)}</b> and total amount to be paid (after tax) is <b>Rp. {GeneralService.getNumberWithSeparator(countNewOutcomeInvoiceAmount().amount)}</b>. IF NOT, the amount of the invoice will not be changed and deposit amount will only be counted as unpaid amount from the invoice.</Text>
                            </Stack>
                            <Checkbox label={"This affect invoice amount"}
                                disabled={!hasOverwriteOutcomeInvoiceAccess}
                                checked={affectInvoiceAmount}
                                onChange={(evt, checked) => {
                                    setAffectInvoiceAmount(checked || false);
                                    regenerateOutomeRelatedOrdersAmount();
                                }} />
                            {!hasOverwriteOutcomeInvoiceAccess ? <Text size={'small'} className={'color-red'}>You don't have access to overwrite paid invoice.</Text> : null}
                        </Stack>
                        {affectInvoiceAmount ? <>
                            <Stack className={'divider'}></Stack>
                            <Stack>
                                <Label size={'xsmall'}>Related orders</Label>
                                {relatedOutcomeOrders && relatedOutcomeOrders.length < 1 ? <Text size={'small'} style={{fontStyle: 'italic'}}>This invoice does not have any related order</Text> : null}
                                {relatedOutcomeOrders && relatedOutcomeOrders.length > 0 ? <>
                                    <DetailsList items={relatedOutcomeOrders}
                                        selectionMode={SelectionMode.none}
                                        isHeaderVisible={true}
                                        compact={true}
                                        cellStyleProps={{cellLeftPadding: 0, cellRightPadding: 5, cellExtraRightPadding: 0}}
                                        columns={[
                                            {
                                                key: 'idx',
                                                name: "No.",
                                                minWidth: 25,
                                                maxWidth: 25,
                                                isPadded: false,
                                                onRender: (item: IOutcomeOrderResourceShortProps, idx?: number) => {
                                                    return <Stack verticalAlign={'center'} styles={{root: {height: '100%'}}}>
                                                        <Text>{(idx || 0) + 1}.</Text>
                                                    </Stack>
                                                }
                                            },
                                            {
                                                key: 'order',
                                                name: "Order Number",
                                                minWidth: 100,
                                                onRender: (item: IOutcomeOrderResourceShortProps) => {
                                                    return <Stack verticalAlign={'center'} styles={{root: {height: '100%'}}}>
                                                        <Label size={'small'}>#{OrderService.getOrderNumber(item.order.orderNumber)}</Label>
                                                    </Stack>
                                                }
                                            },
                                            {
                                                key: 'amount',
                                                name: "New Amount",
                                                minWidth: 175,
                                                maxWidth: 175,
                                                onRender: (item: IOutcomeOrderResourceShortProps) => {
                                                    return <Stack verticalAlign={'center'} styles={{root: {height: '100%'}}}>
                                                        <TextField prefix={'Rp'}
                                                            value={item.amount}
                                                            onChange={(evt, value) => {
                                                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                                                    const _relatedOutcomeOrders = relatedOutcomeOrders;
                                                                    
                                                                    const idx = _relatedOutcomeOrders.findIndex((i) => i.id === item.id);
                                                                    _relatedOutcomeOrders[idx].amount = value || "";

                                                                    setRelatedOutcomeOrders([..._relatedOutcomeOrders]);
                                                                }
                                                            }} />
                                                    </Stack>
                                                }
                                            }
                                        ]} />
                                    {outcomeRelatedOrdersAmountDifference() !== 0 ? <Text className={'color-red'} size={'small'}>Amount missmatch. There is Rp. {GeneralService.getNumberWithSeparator(outcomeRelatedOrdersAmountDifference())} different between total amount in related orders and new invoice amount.</Text> : null}
                                </> : null}
                            </Stack>
                        </> : null}
                    </> : null}
                </> : null}
                {/*!data.employee ? <SelectInvoice label={"Select outcome invoice"}
                    disabled={props.outcomeInvoiceId !== undefined}
                    selected={data.outcomeInvoice}
                    onChange={(invoice) => {
                        let _data = data;
                        _data.outcomeInvoice = invoice;

                        _data.company = undefined;
                        _data.employee = undefined;
                        _data.vendor = invoice?.vendor;

                        setData({ ..._data });
                    }} /> : null}
                <Stack className={'divider'}></Stack>
                {!data.employee && !data.company ? <VendorField label={"Select vendor"}
                    selected={data.vendor}
                    allowPending={true}
                    disabled={(data.outcomeInvoice?.vendor !== undefined) || props.vendorId !== undefined}
                    onChange={(vendor) => {
                        let _data = data;
                        data.vendor = vendor;
                        setData({ ..._data });
                    }} /> : null}
                {!data.vendor && !data.company ? <SelectEmployee label={"Select employee"}
                    selected={data.employee}
                    disabled={(data.outcomeInvoice?.vendor !== undefined) || props.employeeId !== undefined}
                    onChange={(employee) => {
                        let _data = data;
                        data.employee = employee;
                        setData({ ..._data });
                    }} /> : null}
                {!data.employee && !data.vendor ? <SelectCompany label={"Select customer"}
                    selected={data.company}
                    disabled={(data.outcomeInvoice?.vendor !== undefined) || props.companyId !== undefined}
                    onChange={(company) => {
                        let _data = data;
                        data.company = company;
                        setData({ ..._data });
                    }} /> : null}
                <Stack className={'divider'}></Stack>
                <TextField label={'Deposit amount'}
                    value={data.amount}
                    prefix={'Rp'}
                    required={true}
                    errorMessage={error.amount}
                    onChange={(evt, value) => {
                        if ((value || "").trim() === "" || !isNaN(Number(value))) {
                            data.amount = value || "";

                            let max = undefined;
                            if (data.outcomeInvoice) {
                                max = Number(data.outcomeInvoice.paid + "");
                            }

                            const validation = ValidationService.combination(value, ['required', 'limit', 'number'], { maxChars: 20, max });
                            error.amount = validation.message;

                            setData({ ...data });
                            setError({ ...error });
                        }
                    }} />
                {(!relatedOutcomeOrders || (relatedOutcomeOrders || []).length > 0) && data.outcomeInvoice ? <Stack className={'divider'}></Stack> : null}
                {!relatedOutcomeOrders && data.outcomeInvoice ? <LoadingComponent labelPosition={'right'} spinnerPosition={'baseline'} label={"Retrieving orders related to this invoice ..."} /> : null}
                {(relatedOutcomeOrders || []).length > 0 && data.outcomeInvoice ? <Stack>

                </Stack> : null*/}
            </> : null}
        </Stack>
        {activeSurface === 'password.confirm' ? <PasswordConfirmation onCancelled={() => setActiveSurface(undefined)} onConfirmed={_onSubmit} /> : null}
    </Panel>
};

export default CreateDepositForm;
